<template>
  <ResourceDialog
    @save="$emit('save', draft)"
    ref="form"
    :max-width="1000"
    :processing="processing"
    :save-button-disabled="saveDisabledDueToProgramAgeValidation"
    :save-button-disabled-tooltip="saveDisabledDueToProgramAgeValidationTooltip"
    :title="terms.program"
    text-class="bg-super-light-blue pt-6 pb-6"
    closeable
    fullscreen
  >
    <template
      v-if="draft"
      #form
    >
      <v-card
        v-if="!programLockEnabled && !requireProgramAgeGroupSelection"
        class="mb-4"
        border
        tile
      >
        <v-card-text>
          <v-row v-if="draft.schema_id && schema">
            <LabeledSimpleSelect
              v-if="
                schema?.definition?.properties?.name?.enum &&
                schema.definition.properties.name.enum.length > 0
              "
              v-model="draft.name"
              :disabled="isSpecialist && !programsEdit"
              :items="nameOptions"
              :schema-id="draft.schema_id"
              data-testid="edit_program_name"
              field="name"
              item-title="text"
              item-value="value"
              message="Name"
            />
            <LabeledTextfield
              v-else
              v-model="draft.name"
              :disabled="isSpecialist && !programsEdit"
              :schema-id="draft.schema_id"
              data-testid="edit_program_name"
              field="name"
              message="Name"
            />
          </v-row>
        </v-card-text>
      </v-card>

      <v-card
        class="mb-4"
        border
        tile
      >
        <v-card-text>
          <v-row>
            <LabeledTextarea
              v-model="draft.public_notice"
              :disabled="isSpecialist && !programsEdit"
              :rows="1"
              :schema-id="draft.schema_id"
              field="public_notice"
              message="Public notice"
              auto-grow
            />
          </v-row>
        </v-card-text>
      </v-card>

      <LabeledSection
        :fields="['age_min', 'age_max', 'dob_start_date', 'dob_end_date']"
        :hide-top-divider="isSpecialist && !programsEdit"
        :schema-id="draft.schema_id"
        :subtitle="AGES_SUBTITLE_TEXT"
        class="mb-4"
        title="Ages"
      >
        <template #form>
          <v-row dense>
            <LabeledSimpleSelect
              v-if="requireProgramAgeGroupSelection"
              v-model="selectedProgramAgeGroupId"
              :disabled="isSpecialist && !programsEdit"
              :items="programAgeGroups"
              :schema-id="draft.schema_id"
              cols="12"
              data-testid="edit_program_age_group"
              field="age_group_ids"
              item-title="name"
              item-value="id"
              message="Age group"
              clearable
            />
            <LabeledSimpleSelect
              v-else
              v-model="draft.age_group_ids"
              :disabled="isSpecialist && !programsEdit"
              :items="programAgeGroups"
              :schema-id="draft.schema_id"
              cols="12"
              field="age_group_ids"
              item-title="name"
              item-value="id"
              chips
              multiple
            />
          </v-row>

          <v-row v-if="!programLockEnabled">
            <LabeledAgeMonthsYears
              v-model="draft.age_min"
              :disabled="isSpecialist && !programsEdit"
              :max-bound-months="programAgeRangeBounds.max"
              :message="schema?.definition?.properties?.age_min?.alias || 'Youngest age accepted'"
              :min-bound-months="programAgeRangeBounds.min"
              :rules="[ageMinAtOrBelowAgeMaxRule]"
              :schema-id="draft.schema_id"
              cols="6"
              data-testid="edit_program_age_min"
              field="age_min"
              md="5"
            />

            <LabeledAgeMonthsYears
              v-model="draft.age_max"
              :disabled="isSpecialist && !programsEdit"
              :max-bound-months="programAgeRangeBounds.max"
              :message="schema?.definition?.properties?.age_max?.alias || 'Oldest age accepted'"
              :min-bound-months="programAgeRangeBounds.min"
              :rules="[ageMinAtOrBelowAgeMaxRule]"
              :schema-id="draft.schema_id"
              cols="6"
              data-testid="edit_program_age_max"
              field="age_max"
              md="5"
            />
          </v-row>

          <v-row>
            <LabeledDatePicker
              v-model="draft.dob_start_date"
              :disabled="isSpecialist && !programsEdit"
              :schema-id="draft.schema_id"
              cols="6"
              field="dob_start_date"
              message="Date of birth start date"
            />

            <LabeledDatePicker
              v-model="draft.dob_end_date"
              :disabled="isSpecialist && !programsEdit"
              :schema-id="draft.schema_id"
              cols="6"
              field="dob_end_date"
              message="Date of birth end date"
            />
          </v-row>
        </template>
      </LabeledSection>

      <LabeledSection
        :fields="['operating_start_date', 'operating_end_date']"
        :schema-id="draft.schema_id"
        class="mb-4"
        title="Dates"
      >
        <template #form>
          <v-row>
            <LabeledDatePicker
              v-model="draft.operating_start_date"
              :schema-id="draft.schema_id"
              cols="6"
              field="operating_start_date"
              message="Start date"
            />

            <LabeledDatePicker
              v-model="draft.operating_end_date"
              :schema-id="draft.schema_id"
              cols="6"
              field="operating_end_date"
              message="End date"
            />
          </v-row>
        </template>
      </LabeledSection>

      <LabeledSection
        :fields="['desired_capacity', 'license_capacity']"
        :schema-id="draft.schema_id"
        :subtitle="
          `Desired capacity is the number of children you would like to ${terms.enroll.toLowerCase()}` +
          ` in this ${terms.program.toLowerCase()}. This must be equal to or less than your licensed capacity.`
        "
        class="mb-4"
        title="What is the capacity?"
      >
        <template #form>
          <v-row>
            <LabeledTextfield
              v-model="draft.desired_capacity"
              :message="schema?.definition.properties.desired_capacity.alias || 'Desired capacity'"
              :schema-id="draft.schema_id"
              cols="6"
              data-cy="desired_capacity_input"
              field="desired_capacity"
              type="number"
            />

            <template v-if="isSpecialist || draft.license_capacity > 0">
              <LabeledTextfield
                v-model="draft.license_capacity"
                :disabled="!isSpecialist"
                :locked="!isSpecialist ? 'lock' : ''"
                :schema-id="draft.schema_id"
                cols="6"
                field="license_capacity"
                message="Licensed capacity"
                type="number"
              />
            </template>
          </v-row>
        </template>
      </LabeledSection>

      <LabeledSection
        :fields="['rate_weekly', 'is_donation_based', 'is_free']"
        :schema-id="draft.schema_id"
        class="mb-4"
        title="Cost"
      >
        <template #form>
          <template v-if="!draft.is_free">
            <div class="mb-4">
              <div class="mb-2">
                <v-row
                  v-show="draft.rate_hourly || moreRatesVisible"
                  dense
                >
                  <LabeledTextfield
                    v-model="draft.rate_hourly"
                    :schema-id="draft.schema_id"
                    class="mxw-300"
                    field="rate_hourly"
                    prepend-inner-icon="attach_money"
                    type="number"
                  />
                </v-row>

                <v-row
                  v-show="draft.rate_daily || moreRatesVisible"
                  dense
                >
                  <LabeledTextfield
                    v-model="draft.rate_daily"
                    :schema-id="draft.schema_id"
                    class="mxw-300"
                    field="rate_daily"
                    prepend-inner-icon="attach_money"
                    type="number"
                  />
                </v-row>

                <v-row
                  v-show="showWeeklyRate"
                  dense
                >
                  <LabeledTextfield
                    v-model="draft.rate_weekly"
                    :schema-id="draft.schema_id"
                    class="mxw-300"
                    field="rate_weekly"
                    prepend-inner-icon="attach_money"
                    type="number"
                  />
                </v-row>

                <v-row
                  v-show="draft.rate_monthly || moreRatesVisible"
                  dense
                >
                  <LabeledTextfield
                    v-model="draft.rate_monthly"
                    :schema-id="draft.schema_id"
                    class="mxw-300"
                    field="rate_monthly"
                    prepend-inner-icon="attach_money"
                    type="number"
                  />
                </v-row>

                <v-row
                  v-show="draft.rate_bimonthly || moreRatesVisible"
                  dense
                >
                  <LabeledTextfield
                    v-model="draft.rate_bimonthly"
                    :schema-id="draft.schema_id"
                    class="mxw-300"
                    field="rate_bimonthly"
                    prepend-inner-icon="attach_money"
                    type="number"
                  />
                </v-row>

                <v-row
                  v-show="draft.rate_biweekly || moreRatesVisible"
                  dense
                >
                  <LabeledTextfield
                    v-model="draft.rate_biweekly"
                    :schema-id="draft.schema_id"
                    class="mxw-300"
                    field="rate_biweekly"
                    prepend-inner-icon="attach_money"
                    type="number"
                  />
                </v-row>
              </div>

              <v-row v-show="!moreRatesVisible">
                <v-col>
                  <v-btn
                    @click="moreRatesVisible = true"
                    append-icon="expand_more"
                    variant="outlined"
                  >
                    {{ $bct('Show all rate options') }}
                  </v-btn>
                </v-col>
              </v-row>
            </div>
          </template>

          <v-row dense>
            <LabeledCheckbox
              v-model="draft.is_free"
              :schema-id="draft.schema_id"
              checkbox-text="Offered for free"
              field="is_free"
            />

            <LabeledCheckbox
              v-model="draft.is_donation_based"
              :schema-id="draft.schema_id"
              field="is_donation_based"
            />
          </v-row>
        </template>
      </LabeledSection>

      <LabeledSection
        :fields="['hours', 'program_types', 'regular_hours', 'season']"
        :schema-id="draft.schema_id"
        class="mb-4"
        title="Schedule"
      >
        <template #form>
          <v-row>
            <LabeledSelect
              v-model="draft.season"
              :message="
                schema?.definition.properties.season.alias ||
                `Which ${terms.programs.toLowerCase()} do you offer?`
              "
              :schema-id="draft.schema_id"
              field="season"
            />

            <LabeledSelect
              v-model="draft.program_types"
              id="manager_program_program_types"
              :message="schema?.definition.properties.program_types.alias || 'Type'"
              :schema-id="draft.schema_id"
              field="program_types"
              tracked
            />
          </v-row>

          <v-row>
            <LabeledSwitch
              v-model="draft.regular_hours"
              subtitle="The operating hours are the same as the location's hours."
              title="Same as location hours"
            />
            <v-col v-show="!draft.regular_hours">
              <v-row
                v-for="(day, index) in draft.hours"
                :key="index"
                class="mb-2"
                dense
              >
                <v-col style="padding-top: 20px; max-width: 100px">
                  <div class="c-black bc-body">
                    {{ $bct($a.assets.weekdays[day.day]) }}
                  </div>
                </v-col>

                <v-col style="max-width: 100px">
                  <v-switch
                    v-model="draft.hours[index].included"
                    :disabled="
                      draft.regular_hours ||
                      !schema?.definition.properties.hours.editRoles.includes($role)
                    "
                    :label="draft.hours[index].included ? $bct('Open') : $bct('Closed')"
                    :readonly="draft.regular_hours"
                    color="secondary"
                    hide-details
                  />
                </v-col>

                <v-col>
                  <LabeledOpenClose
                    @change="updateDay(index, $event)"
                    :hard-lock="!schema?.definition.properties.hours.editRoles.includes($role)"
                    :model-value="[day.open, day.close]"
                    :step-value="5"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </template>
      </LabeledSection>

      <LabeledSection
        :fields="['contact_email', 'contact_name', 'contact_phone', 'description']"
        :schema-id="draft.schema_id"
        class="mb-4"
        title="Additional information (optional)"
      >
        <template #form>
          <v-row>
            <LabeledTextarea
              v-model="draft.description"
              :schema-id="draft.schema_id"
              field="description"
              message="Description"
            />

            <LabeledTextfield
              v-model="draft.contact_name"
              :schema-id="draft.schema_id"
              field="contact_name"
              message="Contact name"
            />

            <LabeledTextfield
              v-model="draft.contact_email"
              :schema-id="draft.schema_id"
              field="contact_email"
              message="Contact email"
            />

            <LabeledTextfield
              v-model="draft.contact_phone"
              :schema-id="draft.schema_id"
              field="contact_phone"
              message="Contact phone"
            />
          </v-row>
        </template>
      </LabeledSection>

      <LabeledSection
        :active="customFields.length > 0"
        class="mb-4"
        title="Other"
      >
        <template #form>
          <v-row
            v-for="field in customFields"
            :key="field[0]"
          >
            <CustomField
              :field="field[0]"
              :model-value="draft"
              :schema-id="schema.id"
              :show-title="true"
              class="fs-16"
            />
          </v-row>
        </template>
      </LabeledSection>

      <v-card
        v-if="subsidyPrograms.length > 0"
        class="mb-4"
        border
        tile
      >
        <v-card-text>
          <v-row>
            <LabeledSimpleSelect
              v-model="draft.subsidy_program_id"
              @change="onSubsidyProgramChanged"
              :hard-lock="subsidyProgramLocked"
              :items="subsidyPrograms"
              :schema-id="draft.schema_id"
              :tooltip-text="subsidyProgramLocked ? SUBSIDY_PROGRAM_LOCK_TOOLTIP : null"
              cols="12"
              field="subsidy_program_id"
              item-title="name"
              item-value="id"
              message="Subsidy program"
              tooltip-color="warning"
              tooltip-icon="lock"
              clearable
            />
          </v-row>
          <v-row v-if="shouldShowProviderProgramRequirements">
            <LabeledSimpleSelect
              v-model="draft.provider_program_requirements_id"
              :items="providerProgramRequirements"
              :schema-id="draft.schema_id"
              cols="12"
              field="provider_program_requirements_id"
              item-title="name"
              item-value="id"
              message="Provider program requirements"
              clearable
            />
          </v-row>
        </v-card-text>
      </v-card>
    </template>
  </ResourceDialog>
</template>

<script setup>
import { useStore } from 'vuex';

import CustomField from '@/shared/components/CustomField.vue';
import LabeledAgeMonthsYears from '@/shared/components/form/LabeledAgeMonthsYears.vue';
import LabeledCheckbox from '@/shared/components/form/LabeledCheckbox.vue';
import LabeledDatePicker from '@/shared/components/form/LabeledDatePicker.vue';
import LabeledOpenClose from '@/shared/components/form/LabeledOpenClose.vue';
import LabeledSection from '@/shared/components/form/LabeledSection.vue';
import LabeledSelect from '@/shared/components/form/LabeledSelect.vue';
import LabeledSimpleSelect from '@/shared/components/form/LabeledSimpleSelect.vue';
import LabeledSwitch from '@/shared/components/form/LabeledSwitch.vue';
import LabeledTextarea from '@/shared/components/form/LabeledTextarea.vue';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import useApi from '@/shared/composables/useApi';
import useProgramAgeGroupSelection from '@/shared/composables/useProgramAgeGroupSelection';
import useRole from '@/shared/composables/useRole';
import useRolePermissions from '@/shared/composables/useRolePermissions';
import useTerms from '@/shared/composables/useTerms';

const AGES_SUBTITLE_TEXT =
  'Based on your license, you are only permitted to serve the ' +
  'age groups selected. If you have questions, please contact your organization administration.';

const SUBSIDY_PROGRAM_LOCK_TOOLTIP =
  'Subsidy program association cannot be updated when enrollments exist. ' +
  'Please create a new provider program.';

const store = useStore();
const { api: Api } = useApi();
const { isSpecialist } = useRole();
const { programsEdit } = useRolePermissions();
const { terms } = useTerms();

const programLockEnabled = computed(() => {
  return store.state.pages?.Provider?.features?.enable_program_lock === true;
});

const $bct = inject('translator');

const props = defineProps({
  ageGroups: {
    type: Array,
    default: null,
  },
  processing: {
    type: Boolean,
    default: false,
  },
  subsidyPrograms: {
    type: Array,
    default: () => [],
  },
});

defineEmits(['save']);
defineExpose({ close, open });

const customFields = ref([]);
const draft = ref(null);
const form = ref(null);
const moreRatesVisible = ref(false);
const providerProgramRequirements = ref([]);
const schema = ref(null);

const programAgeGroups = computed(() => {
  if (props.ageGroups?.length > 0) {
    return props.ageGroups;
  }
  return store.state.ageGroups.filter((ageGroup) => !ageGroup.hidden);
});

const {
  ageMinAtOrBelowAgeMaxRule,
  initializeProgramAgeGroupSelection,
  programAgeRangeBounds,
  requireProgramAgeGroupSelection,
  saveDisabledDueToProgramAgeValidation,
  saveDisabledDueToProgramAgeValidationTooltip,
  selectedProgramAgeGroupId,
} = useProgramAgeGroupSelection({
  draft,
  schema,
  ageGroups: programAgeGroups,
  translate: $bct,
});

const nameOptions = computed(() => {
  const nameProperty = schema.value?.definition?.properties?.name;
  // These are stored as enum in the BE but called options in FE (confusing)
  const optionsArray = nameProperty?.enum;

  // Prevent mapping error for undefined/null
  if (!optionsArray) return [];

  return optionsArray.map((option) => ({
    text: option,
    value: option,
  }));
});

const shouldShowProviderProgramRequirements = computed(() => {
  return draft.value?.subsidy_program_id && props.subsidyPrograms.length > 0;
});

const subsidyProgramLocked = computed(() => {
  return Boolean(draft.value?.id) && draft.value?.has_enrollments === true;
});

const showWeeklyRate = computed(() => {
  if (!draft.value) return false;

  const otherRates =
    draft.value.rate_hourly ||
    draft.value.rate_daily ||
    draft.value.rate_monthly ||
    draft.value.rate_bimonthly ||
    draft.value.rate_biweekly;

  return draft.value.rate_weekly || moreRatesVisible.value || !otherRates;
});

function close() {
  draft.value = null;
  form.value.close();
}

async function loadProviderProgramRequirements() {
  if (shouldShowProviderProgramRequirements.value) {
    const params = {
      owner_id: draft.value.subsidy_program_id,
      owner_type: 'SubsidyProgram',
      provider_program_requirements: true,
    };
    const response = isSpecialist
      ? await Api.organization.funding_source.index(params)
      : await Api.shared.fundingSource.index(params);
    const fundingSources = response?.data ?? [];

    providerProgramRequirements.value = isSpecialist
      ? fundingSources.filter((fundingSource) => fundingSource.provider_program_requirements)
      : fundingSources;
  } else {
    providerProgramRequirements.value = [];
  }
}

async function onSubsidyProgramChanged() {
  draft.value.provider_program_requirements_id = null;
  await loadProviderProgramRequirements();
}

async function open(draftObject) {
  draft.value = JSON.parse(JSON.stringify(draftObject));

  schema.value = store.state.schemas[draft.value.schema_id];

  // Have Age min & max fields use schema defaults
  initializeProgramAgeGroupSelection();
  customFields.value = Object.entries(schema.value.definition.properties.custom.properties).filter(
    (property) => (property[1].editRoles || []).includes(store.state.role),
  );

  await loadProviderProgramRequirements();

  const title = draft.value.id ? draft.value.name : 'New program';
  form.value.open(null, title);
}

function updateDay(index, day) {
  const [open, close] = day;
  draft.value.hours[index].open = open;
  draft.value.hours[index].close = close;
}
</script>
